/**
 * Language Context Provider
 * 
 * Manages application language state and i18n integration. Handles language
 * changes, RTL support, persistence in localStorage, and synchronization with
 * backend user preferences. Updates document direction and language attributes.
 * 
 * Uses SSR-safe DOM utilities for document manipulation.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import i18n from '@/app/config/i18n/config/i18n.js';
import {
  setDocumentProperty,
  getLocalStorageItem,
  setLocalStorageItem,
  isBrowser,
} from '@/shared/utils/dom/domUtils.js';
import logger from '@/shared/utils/logger.js';

const LanguageContext = createContext(null);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    // Return default values if context is not available (fallback for edge cases)
    logger.warn('useLanguage called outside LanguageProvider, using defaults');
    return {
      currentLanguage: i18n.language || 'en',
      changeLanguage: async (lng) => {
        if (isBrowser()) {
          await i18n.changeLanguage(lng);
        }
      },
      isLoading: false,
      t: i18n.t,
      i18n,
    };
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language || 'en');
  const [isLoading, setIsLoading] = useState(true);

  const changeLanguage = useCallback(async (lng) => {
    if (!isBrowser()) {return;}

    try {
      await i18n.changeLanguage(lng);
      setCurrentLanguage(lng);
      setDocumentProperty('lang', lng);
      
      setLocalStorageItem('i18nextLng', lng);
      
      try {
        const userDataStr = getLocalStorageItem('userData', '{}');
        const userData = JSON.parse(userDataStr);
        if (!userData.preferences) {
          userData.preferences = {};
        }
        userData.preferences.language = lng;
        setLocalStorageItem('userData', JSON.stringify(userData));
        
        try {
          const { profileService } = await import('@/features/settings/services');
          if (getLocalStorageItem('isAuthenticated') === 'true') {
            profileService.updateUserPreferences({
              language: lng,
              currency: userData.preferences?.currency || 'GHS',
              notification_preference: userData.preferences?.notificationPreference || userData.preferences?.notification_preference || 'email',
            }).catch(err => {
              logger.warn('Failed to sync language preference to backend', { error: err });
            });
          }
        } catch (err) {
          logger.warn('Could not sync language to backend', { error: err });
        }
      } catch (error) {
        logger.error('Error saving language preference', { error });
      }
    } catch (error) {
      logger.error('Error changing language', { error });
    }
  }, []);

  useEffect(() => {
    if (!isBrowser()) {
      setIsLoading(false);
      return;
    }

    const initializeLanguage = async () => {
      try {
        const userDataStr = getLocalStorageItem('userData', '{}');
        const userData = JSON.parse(userDataStr);
        const savedLanguage = userData.preferences?.language || 
                             getLocalStorageItem('i18nextLng') || 
                             i18n.language || 
                             'en';
        
        await changeLanguage(savedLanguage);
      } catch (error) {
        logger.error('Error initializing language', { error });
        const browserLang = getLocalStorageItem('i18nextLng') || i18n.language || 'en';
        await changeLanguage(browserLang);
      } finally {
        setIsLoading(false);
      }
    };
    
    initializeLanguage();
  }, [changeLanguage]);

  const value = {
    currentLanguage,
    changeLanguage,
    isLoading,
    t: i18n.t, // Translation function
    i18n,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

